import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Smartphone, Upload, Wallet, ArrowDownCircle,
  ArrowUpCircle, Package, Users, Receipt, User, LogOut, Menu, X, Shield,
  Lightbulb, Video, ClipboardList, PenLine
} from 'lucide-react';

const navGroups = [
  {
    label: 'Main',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true, color: 'violet' },
    ],
  },
  {
    label: 'Tasks',
    items: [
      { to: '/dashboard/tasks', icon: Smartphone, label: 'WhatsApp Tasks', color: 'orange' },
      { to: '/dashboard/upload', icon: Upload, label: 'Upload Screenshot', color: 'orange' },
      { to: '/dashboard/trivia', icon: Lightbulb, label: 'Trivia', color: 'orange' },
      { to: '/dashboard/watch', icon: Video, label: 'Watch & Earn', color: 'orange' },
      { to: '/dashboard/surveys', icon: ClipboardList, label: 'Surveys', color: 'orange' },
      { to: '/dashboard/blog', icon: PenLine, label: 'Blogging', color: 'orange' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/dashboard/packages', icon: Package, label: 'Buy Package', color: 'blue' },
      { to: '/dashboard/deposit', icon: ArrowDownCircle, label: 'Deposit', color: 'green' },
      { to: '/dashboard/withdraw', icon: ArrowUpCircle, label: 'Withdraw', color: 'pink' },
      { to: '/dashboard/wallet', icon: Wallet, label: 'Wallet', color: 'slate' },
    ],
  },
  {
    label: 'Account',
    items: [
      { to: '/dashboard/referrals', icon: Users, label: 'Referrals', color: 'blue' },
      { to: '/dashboard/transactions', icon: Receipt, label: 'Transactions', color: 'slate' },
      { to: '/dashboard/profile', icon: User, label: 'Profile', color: 'indigo' },
    ],
  },
];

export const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };
  const isActive = user?.status === 'active';

  // Inactive accounts: NO sidebar, NO nav, NO topbar.
  // Only the activation screen at /dashboard is allowed; everything else redirects there.
  if (!isActive) {
    if (location.pathname !== '/dashboard') {
      return <Navigate to="/dashboard" replace />;
    }
    return (
      <div style={{ minHeight: '100vh', width: '100%' }}>
        <Outlet />
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-text">Drivenwave</span>
            <span className="logo-sub">Marketing</span>
          </div>
          <button className="close-btn" onClick={() => setSidebarOpen(false)}><X size={20} /></button>
        </div>

        <div className="user-info">
          <div className="avatar">{user?.full_name?.[0]?.toUpperCase()}</div>
          <div>
            <p className="user-name">{user?.full_name}</p>
            <span className="status-badge" style={{ background: '#22c55e' }}>ACTIVE</span>
          </div>
        </div>

        <div className="wallet-pill">
          <span>Wallet</span>
          <strong>KES {Number(user?.wallet_balance || 0).toLocaleString()}</strong>
        </div>

        <nav className="sidebar-nav">
          {navGroups.map((group) => (
            <div className="nav-group" key={group.label}>
              <div className="nav-group-label">{group.label}</div>
              {group.items.map(({ to, icon: Icon, label, end, color }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive: active }) => `nav-item ${active ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className={`nav-icon nav-icon-${color}`}><Icon size={18} /></span>
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          ))}

          {user?.role === 'admin' && (
            <div className="nav-group">
              <div className="nav-group-label">Admin</div>
              <NavLink to="/admin" className="nav-item admin-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon nav-icon-gold"><Shield size={18} /></span>
                <span>Admin Panel</span>
              </NavLink>
            </div>
          )}
        </nav>

        <button className="nav-item logout-btn" onClick={handleLogout}>
          <span className="nav-icon nav-icon-red"><LogOut size={18} /></span>
          <span>Logout</span>
        </button>
      </aside>

      {sidebarOpen && <div className="overlay" onClick={() => setSidebarOpen(false)} />}

      <main className="main-content">
        <header className="topbar">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}><Menu size={22} /></button>
          <div className="topbar-right">
            {user?.package_level && user.package_level !== 'none' && (
              <span className="package-badge">{user.package_level.toUpperCase()}</span>
            )}
          </div>
        </header>
        <div className="page-content"><Outlet /></div>
      </main>
    </div>
  );
};
